import { z } from 'zod';
import { baseEntitySchema, idSchema, instantSchema } from './common.js';
import { customValuesSchema } from './fields.js';
import { filterSchema, sortSchema } from './filters.js';
/**
 * The client side of the model (RELAY-949), agreed 2026-09-24:
 *
 *   company  — the client organisation; its domain is unique per organisation.
 *   contact  — a person at a client, emails/phones shaped like a candidate's.
 *   project  — the client engagement: exactly ONE company, a Project Stage, an owner.
 *   project_contacts — contacts as line items on a project (role, one primary); a contact can
 *              be on many projects.
 *   deal     — a payment line under a project: value, currency, billing model, a Deal Status.
 *
 * Stages are rows of the RELAY-948 pipelines, referenced by id, so a renamed stage never breaks
 * a project, a deal or their history. Every stage move writes a history row, an audit row and
 * an activity row in the same transaction.
 */
/**
 * Ids the same record has in other systems — `{ "recruitcrm": "184233", "quickbooks": "INV-88" }`.
 * One id per system; the key is the system's name.
 */
export const externalIdsSchema = z
    .record(z
    .string()
    .regex(/^[a-z][a-z0-9_]{0,39}$/, 'a lower-case system name, e.g. recruitcrm or quickbooks'), z.string().trim().min(1).max(200))
    .refine((v) => Object.keys(v).length <= 20, { message: 'at most 20 external ids' });
/** `https://www.Example.com/about` → `example.com`. What company uniqueness and matching use. */
export function normaliseDomain(v) {
    const d = (v ?? '')
        .trim()
        .toLowerCase()
        .replace(/^[a-z][a-z0-9+.-]*:\/\//, '')
        .replace(/^www\./, '')
        .replace(/[/?#:].*$/, '')
        .replace(/\.+$/, '');
    return d || null;
}
const domainSchema = z
    .string()
    .trim()
    .max(253)
    .transform((v, ctx) => {
    const d = normaliseDomain(v);
    if (d && !/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(d)) {
        ctx.addIssue({ code: 'custom', message: 'not a domain, e.g. example.com' });
        return z.NEVER;
    }
    return d;
});
/**
 * A website as people type it (`example.com`, `https://example.com/about`) as an absolute
 * http(s) URL, `https://` when no scheme was given — the same rule Relay and Hriq apply to
 * pasted URLs. Null when blank or not a website (a company name, `localhost`, `javascript:`).
 */
export function normaliseWebsite(v) {
    const s = (v ?? '').trim();
    if (!s || /\s/.test(s))
        return null;
    const raw = /^[a-z][a-z0-9+.-]*:/i.test(s) ? s : `https://${s}`;
    if (!URL.canParse(raw))
        return null;
    const url = new URL(raw);
    return /^https?:$/.test(url.protocol) && url.hostname.includes('.') ? url.href : null;
}
const websiteSchema = z
    .string()
    .trim()
    .max(500)
    .transform((v, ctx) => {
    if (!v)
        return null;
    const url = normaliseWebsite(v);
    if (!url) {
        ctx.addIssue({ code: 'custom', message: 'not a website, e.g. example.com' });
        return z.NEVER;
    }
    return url;
});
const text = (max) => z.string().trim().min(1).max(max);
/** No `max` = no limit (`industry`: RecruitCRM holds values past 120 characters, DATA-1306). */
const optionalText = (max) => (max === undefined ? z.string().trim() : z.string().trim().max(max)).nullable().optional();
// ── Companies ──────────────────────────────────────────────────────────────────────────────
export const companyInputSchema = z.object({
    name: text(200),
    domain: domainSchema.nullable().optional(),
    website: websiteSchema.nullable().optional(),
    industry: optionalText(),
    country: optionalText(120),
    phone: optionalText(60),
    ownerId: idSchema.nullable().optional(),
    externalIds: externalIdsSchema.optional(),
    /** Custom field values by definition key (TS-1751). No default: a PATCH touches only what it carries. */
    custom: customValuesSchema.optional(),
});
export const companyPatchSchema = companyInputSchema
    .partial()
    .refine((p) => Object.keys(p).length > 0, { message: 'nothing to change' });
export const companySchema = baseEntitySchema.extend({
    name: z.string(),
    /** Normalised (`example.com`); unique per organisation among live companies. */
    domain: z.string().nullable(),
    website: z.string().nullable(),
    industry: z.string().nullable(),
    country: z.string().nullable(),
    phone: z.string().nullable(),
    ownerId: idSchema.nullable(),
    externalIds: externalIdsSchema,
    /** Custom field values by definition key (TS-1751); only the fields the caller's role may see. */
    custom: customValuesSchema.default({}),
});
/** A company in the list, with its newest live project (a company should have at most one). */
export const companyListItemSchema = companySchema.extend({ projectId: idSchema.nullable() });
/** The company fields a filter may name (`POST /companies:search`), with the type that decides the operators. */
export const COMPANY_FIELDS = {
    id: 'reference',
    name: 'text',
    domain: 'text',
    website: 'text',
    industry: 'text',
    country: 'text',
    phone: 'text',
    ownerId: 'user',
    createdAt: 'datetime',
    updatedAt: 'datetime',
};
export const COMPANY_SORTABLE = [
    'createdAt',
    'updatedAt',
    'name',
    'industry',
    'country',
];
/** `POST /companies:search`: `q` matches the name or domain; `custom.<key>` filters and sorts too. */
export const companySearchSchema = z.object({
    q: z.string().trim().min(1).max(200).optional(),
    filter: filterSchema.optional(),
    sort: z.array(sortSchema).max(1).optional(),
    cursor: z.string().min(1).optional(),
    limit: z.number().int().min(1).max(200).default(50),
});
// ── Contacts ───────────────────────────────────────────────────────────────────────────────
const contactFields = {
    firstName: optionalText(120),
    lastName: optionalText(120),
    email: z.string().trim().max(320).pipe(z.email()).nullable().optional(),
    phone: optionalText(60),
    altPhone: optionalText(60),
    jobTitle: optionalText(200),
    /** The company the contact works for now; a contact may still sit on other companies' projects. */
    companyId: idSchema.nullable().optional(),
    ownerId: idSchema.nullable().optional(),
    externalIds: externalIdsSchema.optional(),
    /** Custom field values by definition key (TS-1840). No default: a PATCH touches only what it carries. */
    custom: customValuesSchema.optional(),
};
export const contactInputSchema = z
    .object(contactFields)
    .refine((c) => !!(c.firstName || c.lastName || c.email), {
    message: 'a contact needs a name or an email',
});
export const contactPatchSchema = z
    .object(contactFields)
    .partial()
    .refine((p) => Object.keys(p).length > 0, { message: 'nothing to change' });
export const contactSchema = baseEntitySchema.extend({
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    altPhone: z.string().nullable(),
    /** Generated by the database from `email` (lower-cased, trimmed), as on candidates. */
    primaryEmail: z.string().nullable(),
    /** Generated by the database from `phone` (digits only), as on candidates. */
    primaryPhone: z.string().nullable(),
    jobTitle: z.string().nullable(),
    companyId: idSchema.nullable(),
    ownerId: idSchema.nullable(),
    externalIds: externalIdsSchema,
    /** Custom field values by definition key (TS-1840); only the fields the caller's role may see. */
    custom: customValuesSchema.default({}),
});
/** The contact fields a filter may name (`POST /contacts:search`), with the type that decides the operators. */
export const CONTACT_FIELDS = {
    id: 'reference',
    firstName: 'text',
    lastName: 'text',
    email: 'text',
    phone: 'text',
    altPhone: 'text',
    jobTitle: 'text',
    companyId: 'reference',
    ownerId: 'user',
    createdAt: 'datetime',
    updatedAt: 'datetime',
};
export const CONTACT_SORTABLE = [
    'createdAt',
    'updatedAt',
    'firstName',
    'lastName',
    'jobTitle',
];
/**
 * `POST /contacts:search` (TS-1840): `q` matches the name or email, `companyId` narrows to one
 * company; `custom.<key>` filters and sorts too.
 */
export const contactSearchSchema = z.object({
    q: z.string().trim().min(1).max(200).optional(),
    companyId: idSchema.optional(),
    filter: filterSchema.optional(),
    sort: z.array(sortSchema).max(1).optional(),
    cursor: z.string().min(1).optional(),
    limit: z.number().int().min(1).max(200).default(50),
});
// ── Stages as projects and deals carry them ────────────────────────────────────────────────
/** The stage a project or deal is in, resolved from the pipeline so a client renders it directly. */
export const stageRefSchema = z.object({
    id: idSchema,
    name: z.string(),
    slug: z.string(),
    colour: z.string(),
    isTerminal: z.boolean(),
    terminalKind: z.enum(['won', 'lost']).nullable(),
});
/** One stage move, oldest first in `…/stage-history`. `fromStageId` is null for the first stage. */
export const stageHistoryEntrySchema = z.object({
    id: idSchema,
    /** A project's moves name the pipeline they happened in (TS-1747); absent for deals. */
    pipelineId: idSchema.optional(),
    fromStageId: idSchema.nullable(),
    toStageId: idSchema,
    changedBy: idSchema.nullable(),
    changedAt: instantSchema,
});
// ── Projects ───────────────────────────────────────────────────────────────────────────────
export const projectInputSchema = z.object({
    name: text(200),
    /** Exactly one company, always. */
    companyId: idSchema,
    /**
     * On create: the stage it starts in, in that stage's (live) project pipeline, which it joins;
     * the default pipeline's first open stage when omitted. On PATCH: a move within the pipeline
     * that stage belongs to — the project must already be in it (TS-1747).
     */
    stageId: idSchema.optional(),
    ownerId: idSchema.nullable().optional(),
    description: z.string().trim().max(10_000).nullable().optional(),
    externalIds: externalIdsSchema.optional(),
    /** Custom field values by definition key (TS-1751). No default: a PATCH touches only what it carries. */
    custom: customValuesSchema.optional(),
});
/** `companyId` can move the project to another company but never to none. */
export const projectPatchSchema = projectInputSchema
    .partial()
    .refine((p) => Object.keys(p).length > 0, { message: 'nothing to change' });
/**
 * A record's place in one pipeline: a project's (TS-1747) or a candidate's (Candidate Flow). A
 * record is in live pipelines of its type, each with its own stage; archived pipelines are left
 * out. A place that ended (won or lost) can be archived (`archivedAt`): it stays on the record,
 * folded away and off the board, until it is restored.
 */
export const pipelinePlaceSchema = z.object({
    pipelineId: idSchema,
    name: z.string(),
    isDefault: z.boolean(),
    stageId: idSchema,
    stage: stageRefSchema,
    /**
     * Only for a place at a WON or LOST stage: the stage it was in right before it moved there (the newest
     * history row into this stage), so the stepper fills the bar only up to it. Null otherwise, and when
     * unknown (it started at Won or Lost, or its history was imported).
     */
    previousStageId: idSchema.nullable(),
    stageEnteredAt: instantSchema,
    addedAt: instantSchema,
    archivedAt: instantSchema.nullable(),
});
/** A project's place in one pipeline (TS-1747): the record-neutral `pipelinePlaceSchema`. */
export const projectPipelineSchema = pipelinePlaceSchema;
export const projectSchema = baseEntitySchema.extend({
    name: z.string(),
    companyId: idSchema,
    company: z.object({ id: idSchema, name: z.string() }),
    /**
     * The stage in the DEFAULT pipeline, which the Projects board and list show when no other
     * pipeline is picked (TS-1749); null when the project has left the default pipeline.
     */
    stageId: idSchema.nullable(),
    stage: stageRefSchema.nullable(),
    /** Every live pipeline the project is in, the default first, then in the order it joined. */
    pipelines: z.array(projectPipelineSchema),
    ownerId: idSchema.nullable(),
    description: z.string().nullable(),
    externalIds: externalIdsSchema,
    /** Custom field values by definition key (TS-1751); only the fields the caller's role may see. */
    custom: customValuesSchema.default({}),
});
/** Put a record in another pipeline; `stageId` (of that pipeline) defaults to its first open. */
export const pipelinePlaceAddSchema = z.object({
    pipelineId: idSchema,
    stageId: idSchema.optional(),
});
export const projectPipelineAddSchema = pipelinePlaceAddSchema;
/** At most this many projects per bulk add — the ~400 customers of the AM initiative fit. */
export const PROJECT_BULK_MAX = 1000;
/** Put many projects in a pipeline at once (TS-1747); ones already in it are skipped, not moved. */
export const projectBulkPipelineAddSchema = projectPipelineAddSchema.extend({
    projectIds: z
        .array(idSchema)
        .min(1)
        .max(PROJECT_BULK_MAX)
        .refine((ids) => new Set(ids).size === ids.length, { message: 'a project is listed twice' }),
});
export const projectBulkPipelineResultSchema = z.object({
    added: z.number().int(),
    skipped: z.array(z.object({
        projectId: idSchema,
        reason: z.enum(['already_in_pipeline', 'not_found', 'no_permission']),
    })),
});
/**
 * Put many deals or jobs in another pipeline of their type at once (`POST /deals:add-to-pipeline`,
 * `POST /jobs:add-to-pipeline`), as for projects: one already in it, not found or not the caller's
 * to edit is skipped with its reason, never moved.
 */
export const placeBulkAddSchema = pipelinePlaceAddSchema.extend({
    ids: z
        .array(idSchema)
        .min(1)
        .max(PROJECT_BULK_MAX)
        .refine((ids) => new Set(ids).size === ids.length, { message: 'a record is listed twice' }),
});
export const placeBulkAddResultSchema = z.object({
    added: z.number().int(),
    skipped: z.array(z.object({
        id: idSchema,
        reason: z.enum(['already_in_pipeline', 'not_found', 'no_permission']),
    })),
});
export const projectContactInputSchema = z.object({
    contactId: idSchema,
    /** What the person is on this engagement: "Hiring manager", "Billing"… */
    role: optionalText(120),
    /** One primary contact per project; making one primary demotes the previous one. */
    isPrimary: z.boolean().default(false),
});
export const projectContactPatchSchema = z
    .object({ role: optionalText(120), isPrimary: z.boolean() })
    .partial()
    .refine((p) => Object.keys(p).length > 0, { message: 'nothing to change' });
export const projectContactSchema = z.object({
    contactId: idSchema,
    role: z.string().nullable(),
    isPrimary: z.boolean(),
    contact: z.object({
        id: idSchema,
        name: z.string(),
        email: z.string().nullable(),
        phone: z.string().nullable(),
        companyId: idSchema.nullable(),
    }),
    addedAt: instantSchema,
});
// ── Deals ──────────────────────────────────────────────────────────────────────────────────
export const BILLING_MODELS = ['placement_fee', 'monthly', 'hourly'];
export const billingModelSchema = z.enum(BILLING_MODELS);
/** Money as a decimal string (Postgres numeric(14,2)); a number is accepted on the way in. */
const moneySchema = z
    .union([z.string().trim(), z.number().nonnegative()])
    .transform((v) => (typeof v === 'number' ? v.toFixed(2) : v))
    .pipe(z.string().regex(/^\d{1,12}(\.\d{1,2})?$/, 'an amount with at most two decimals'));
/** ISO 4217, upper case. */
const currencySchema = z
    .string()
    .trim()
    .regex(/^[A-Za-z]{3}$/, 'a three-letter currency code')
    .transform((c) => c.toUpperCase());
const dealFields = {
    title: text(200),
    value: moneySchema,
    currency: currencySchema,
    billingModel: billingModelSchema,
    /**
     * A Deal Status; the pipeline's first open stage when omitted. On create it may name a stage of
     * any live Deal Flow pipeline (see `pipelineId`); on a PATCH, the pipeline it belongs to moves.
     */
    stageId: idSchema.optional(),
    expectedCloseDate: z.iso.date().nullable().optional(),
    paidAt: instantSchema.nullable().optional(),
    invoiceRef: optionalText(120),
    externalIds: externalIdsSchema.optional(),
    /** Custom field values by definition key (TS-1840). No default: a PATCH touches only what it carries. */
    custom: customValuesSchema.optional(),
};
export const dealInputSchema = z.object({
    ...dealFields,
    currency: currencySchema.default('USD'),
    /**
     * The Deal Flow pipeline the deal starts in (`stageId`, when given, must be one of its stages;
     * else its first open stage). An EXTRA pipeline puts the deal there too: its status, which is
     * always the default pipeline's, starts at that pipeline's first open stage. Omitted: the default.
     */
    pipelineId: idSchema.optional(),
});
/**
 * Never `projectId`: a deal belongs to its project for life. Built from the fields WITHOUT the
 * create-time default — a `.partial()` of a defaulted field still fills it in, so every PATCH
 * would have reset the currency to USD.
 */
export const dealPatchSchema = z
    .object(dealFields)
    .partial()
    .refine((p) => Object.keys(p).length > 0, { message: 'nothing to change' });
export const dealSchema = baseEntitySchema.extend({
    projectId: idSchema,
    /** Which client the deal is for, so a cross-project list or board card can say (RELAY-1038). */
    project: z.object({ id: idSchema, name: z.string(), companyName: z.string() }),
    title: z.string(),
    value: z.string(),
    currency: z.string(),
    billingModel: billingModelSchema,
    /** The stage in the DEFAULT Deal Flow pipeline, where every deal always has a place. */
    stageId: idSchema,
    stage: stageRefSchema,
    /** Every live Deal Flow pipeline the deal is in, the default first, then in the order it joined. */
    pipelines: z.array(pipelinePlaceSchema),
    expectedCloseDate: z.iso.date().nullable(),
    paidAt: instantSchema.nullable(),
    invoiceRef: z.string().nullable(),
    externalIds: externalIdsSchema,
    /** Custom field values by definition key (TS-1840); only the fields the caller's role may see. */
    custom: customValuesSchema.default({}),
});
/**
 * `POST /deals/{id}:move-status`. Moving to a won stage (Paid) stamps `paidAt` with now unless
 * the caller says when; moving away from it leaves `paidAt` as it was (it records that a
 * payment happened, which a later correction does not undo).
 */
export const dealMoveSchema = z.object({
    stageId: idSchema,
    paidAt: instantSchema.optional(),
    invoiceRef: optionalText(120),
});
// ── Matching (for importers and integrations deciding "link or create") ───────────────────
export const companyMatchRequestSchema = z
    .object({
    domain: domainSchema.nullable().optional(),
    name: z.string().trim().max(200).optional(),
})
    .refine((m) => !!(m.domain || m.name), { message: 'give a domain or a name' });
export const companyMatchSchema = z.object({
    id: idSchema,
    name: z.string(),
    domain: z.string().nullable(),
    /** `domain` is an exact match on the normalised domain; `name` is a similar name. */
    matchedOn: z.enum(['domain', 'name']),
    /** 1 for an exact domain; the trigram similarity (0–1) for a name. */
    score: z.number(),
});
export const contactMatchRequestSchema = z
    .object({
    email: z.string().trim().max(320).optional(),
    phone: z.string().trim().max(60).optional(),
    name: z.string().trim().max(240).optional(),
    companyId: idSchema.optional(),
})
    .refine((m) => !!(m.email || m.phone || m.name), { message: 'give an email, a phone or a name' });
export const contactMatchSchema = z.object({
    id: idSchema,
    name: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    companyId: idSchema.nullable(),
    matchedOn: z.enum(['email', 'phone', 'name']),
    score: z.number(),
});
/**
 * One line of a project's History tab (RELAY-952): an audit row about the project or about one
 * of its deals, jobs or contact links, newest first, with the field-level diff.
 */
export const projectHistoryEntrySchema = z.object({
    id: idSchema,
    occurredAt: instantSchema,
    actor: z.object({ id: z.string().nullable(), label: z.string().nullable() }),
    action: z.string(),
    entityType: z.enum(['projects', 'deals', 'jobs', 'project_contacts', 'project_pipelines']),
    entityId: idSchema.nullable(),
    changes: z
        .record(z.string(), z.object({ old: z.unknown().optional(), new: z.unknown().optional() }))
        .nullable(),
});
/**
 * Find the projects a person is on, by their email (TS-1912). For integrations that only know the
 * client's email — n8n after a Calendly booking — before they move a project with
 * `PATCH /projects/{id}` or `POST /projects/{id}/pipelines`. In the BODY of a POST: a query string
 * would put it in every request log line, trace and access log (review of PR #158), as `:match`
 * does for contacts. Email only: a phone lookup had no caller once reply-driven moves were dropped,
 * and would have needed its own indexes (review of PR #158); add it back with them when needed.
 */
export const projectLookupBodySchema = z.object({
    email: z.string().trim().max(320).pipe(z.email()),
});
/**
 * Slim on purpose (review of PR #158): what an automation needs to move a card, nothing more. No
 * owner, company details or custom fields, so a leaked or over-scoped key exposes as little as
 * possible. Read the project itself (`GET /projects/{id}`) for the rest.
 */
export const projectLookupSchema = z.object({
    /** True when more people or projects matched than one answer holds: the list is not complete. */
    truncated: z.boolean(),
    items: z.array(z.object({
        project: z.object({
            id: idSchema,
            name: z.string(),
            companyId: idSchema,
            pipelines: z.array(projectPipelineSchema),
        }),
        /** The project's contacts (or its company's people) that have this email or number. */
        contactIds: z.array(idSchema),
    })),
});
//# sourceMappingURL=projects.js.map